import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { shiftMasterApi } from '../api/shiftMasterApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  name: '',
  alias: '',
  inHour: '10',
  inMinute: '00',
  inAmPm: 'am',
  outHour: '07',
  outMinute: '00',
  outAmPm: 'pm',
  shiftType: 'Day',
  status: 'Active',
};

export function useShiftMasterEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('id');
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      shiftMasterApi
        .getRecordById(editId)
        .then((record) => {
          if (record) {
            setForm({
              name: record.name || '',
              alias: record.alias || '',
              inHour: record.inHour || '10',
              inMinute: record.inMinute || '00',
              inAmPm: record.inAmPm || 'am',
              outHour: record.outHour || '07',
              outMinute: record.outMinute || '00',
              outAmPm: record.outAmPm || 'pm',
              shiftType: record.shiftType || 'Day',
              status: record.status || 'Active',
            });
          } else {
            appToast.error('Shift record not found.');
            navigate('/payroll/shift-master');
          }
        })
        .catch(() => {
          appToast.error('Failed to load shift details.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [editId, isEdit, navigate]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    if (!form.name.trim()) {
      appToast.error('Shift Name is required.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await shiftMasterApi.updateRecord(editId, form);
        appToast.success('Shift updated successfully!');
      } else {
        await shiftMasterApi.createRecord(form);
        appToast.success('Shift created successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/shift-master');
      } else if (!isEdit) {
        setForm(DEFAULT_FORM_STATE);
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : 'Failed to save Shift details.');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      isEdit,
      editId,
      isLoading,
      isSaving,
    },
    actions: {
      patchForm,
      handleSave,
    },
  };
}
